"use client";

import {
  AlertTriangle,
  Brain,
  CheckCircle2,
  ChevronDown,
  Clipboard,
  Download,
  Info,
  Pencil,
  Play,
  Plus,
  RotateCcw,
  Save,
  Square,
  Terminal,
  Trash2,
  Upload,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useAICheckLocalIdentity } from "@/hooks/useAICheckLocalIdentity";
import { useFreeformReview } from "@/hooks/useFreeformReview";
import { apiUrl } from "@/lib/aicheck/api-base";
import {
  createFreeformTemplateExportBundle,
  type FreeformTemplateExportBundle,
  importFreeformTemplateBundle,
  parseFreeformTemplateBundle,
} from "@/lib/aicheck/freeform-template-storage";
import { estimateTokenCost } from "@/lib/aicheck/model-pricing";
import { hasProjectDataReference } from "@/lib/aicheck/project-data-reference";
import { authService } from "@/services/auth";
import type {
  AggregatedProjectData,
  FreeformCheckResult,
  FreeformCheckTemplate,
  FreeformEvidence,
  FreeformFinding,
  FreeformPartialResult,
  FreeformProgressDetail,
  FreeformProgressEvent,
  OutputLanguage,
  ProjectDataSourceDescriptor,
} from "@/types/aicheck";
import { ProjectDataReferenceButton } from "./shared/EvidenceList";

interface FreeformReviewPanelProps {
  projectId?: number;
  projectData: AggregatedProjectData;
  language: OutputLanguage;
  source?: ProjectDataSourceDescriptor;
  initialTemplateId?: string;
  initialPrompt?: string;
  onProjectDataReferenceClick?: (source: string) => void;
  onResultChange?: (result: FreeformCheckResult | null) => void;
}

function formatBytes(value?: number): string {
  if (!value) return "0 KB";
  return `${Math.max(1, Math.round(value / 1024)).toLocaleString()} KB`;
}

function findingBadgeClass(
  value: FreeformFinding["severity"] | FreeformFinding["finding_type"],
): string {
  switch (value) {
    case "critical":
    case "must_confirm":
      return "bg-red-100 text-red-700";
    case "warning":
    case "should_review":
      return "bg-amber-100 text-amber-700";
    case "data_gap":
      return "bg-blue-100 text-blue-700";
    case "idea":
      return "bg-emerald-100 text-emerald-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
}

function formatDuration(ms?: number): string {
  if (ms == null) return "-";
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
}

function formatCost(value?: number): string {
  return typeof value === "number" ? `$${value.toFixed(4)}` : "-";
}

function freeformResultCost(result: FreeformCheckResult): number | undefined {
  if (typeof result.estimatedCost === "number") return result.estimatedCost;
  if (!result.model) return undefined;
  return estimateTokenCost(result.model, result.tokensUsed);
}

function freeformEvidenceReferenceSource(
  finding: FreeformFinding,
  evidence: FreeformEvidence,
): string | null {
  return (
    [evidence.json_path, evidence.source, finding.json_path].find((source) =>
      hasProjectDataReference(source),
    ) ?? null
  );
}

function eventLabel(event: FreeformProgressEvent): string {
  switch (event.type) {
    case "status":
      return event.message;
    case "tool-progress":
      return event.message;
    case "reasoning-summary":
      return "Analysis summary updated.";
    case "api-progress":
      return event.phase === "streaming"
        ? "Streaming structured output."
        : "Waiting for model output.";
    case "partial-summary":
      return "Summary preview received.";
    case "partial-finding":
      return `Finding ${event.index + 1} preview received.`;
    case "result":
      return "Result received.";
    case "error":
      return event.error;
    case "heartbeat":
      return "";
  }
}

function truncateInline(value: string, maxLength = 180): string {
  const compact = value.replace(/\s+/g, " ").trim();
  return compact.length <= maxLength
    ? compact
    : `${compact.slice(0, maxLength)}...`;
}

function progressEventKey(event: FreeformProgressEvent, index: number): string {
  switch (event.type) {
    case "status":
      return "status";
    case "tool-progress":
      return `tool-progress:${event.itemId ?? "current"}:${
        event.outputIndex ?? "current"
      }`;
    case "reasoning-summary":
      return "reasoning-summary";
    case "api-progress":
      return `api-progress:${event.phase}`;
    case "partial-summary":
      return "partial-summary";
    case "partial-finding":
      return `partial-finding:${event.index}`;
    case "result":
      return `result:${event.data.createdAt}`;
    case "error":
      return `error:${event.error}:${index}`;
    case "heartbeat":
      return `heartbeat:${index}`;
  }
}

function eventDetails(event: FreeformProgressEvent): FreeformProgressDetail[] {
  if (
    "details" in event &&
    Array.isArray(event.details) &&
    event.details.length > 0
  ) {
    return event.details;
  }
  if (event.type === "reasoning-summary" && event.summary) {
    return [{ label: "Summary", value: event.summary, kind: "text" }];
  }
  if (event.type === "partial-summary") {
    return [{ label: "Summary", value: event.summary, kind: "text" }];
  }
  if (event.type === "partial-finding") {
    return [
      {
        label: "Finding",
        value: event.finding.finding,
        kind: "text",
      },
      {
        label: "Recommendation",
        value: event.finding.recommendation,
        kind: "text",
      },
    ];
  }
  return [];
}

function findingCardKey(finding: FreeformFinding, index: number): string {
  return [
    index,
    finding.finding_type,
    finding.severity,
    finding.json_path ?? "",
    finding.item_reference ?? "",
  ].join(":");
}

function ActivityIcon({ event }: { event: FreeformProgressEvent }) {
  if (event.type === "tool-progress") {
    return <Terminal size={14} className="text-blue-600" />;
  }
  if (event.type === "reasoning-summary") {
    return <Brain size={14} className="text-violet-600" />;
  }
  if (event.type === "partial-summary" || event.type === "partial-finding") {
    return <CheckCircle2 size={14} className="text-emerald-600" />;
  }
  if (event.type === "error") {
    return <AlertTriangle size={14} className="text-red-600" />;
  }
  if (event.type === "status" && event.status === "completed") {
    return <CheckCircle2 size={14} className="text-emerald-600" />;
  }
  return <Info size={14} className="text-gray-500" />;
}

function ActivityBadge({ event }: { event: FreeformProgressEvent }) {
  const label =
    event.type === "tool-progress"
      ? "Tool"
      : event.type === "reasoning-summary"
        ? "Reasoning"
        : event.type === "partial-summary" || event.type === "partial-finding"
          ? "Preview"
          : event.type === "error"
            ? "Error"
            : "Status";
  const className =
    event.type === "tool-progress"
      ? "bg-blue-50 text-blue-700 ring-blue-100"
      : event.type === "reasoning-summary"
        ? "bg-violet-50 text-violet-700 ring-violet-100"
        : event.type === "partial-summary" || event.type === "partial-finding"
          ? "bg-emerald-50 text-emerald-700 ring-emerald-100"
          : event.type === "error"
            ? "bg-red-50 text-red-700 ring-red-100"
            : "bg-gray-100 text-gray-700 ring-gray-200";
  return (
    <span
      className={`inline-flex h-5 items-center rounded px-1.5 text-[11px] font-semibold ring-1 ${className}`}
    >
      {label}
    </span>
  );
}

function ActivityDetails({ details }: { details: FreeformProgressDetail[] }) {
  if (details.length === 0) return null;
  return (
    <details className="mt-2 rounded-md border border-gray-200 bg-white">
      <summary className="flex cursor-pointer list-none items-center gap-1.5 px-2 py-1.5 text-xs font-semibold text-gray-500 hover:text-gray-800">
        <ChevronDown size={13} />
        Details
      </summary>
      <div className="space-y-2 border-t border-gray-100 p-2">
        {details.map((detail, index) => (
          <div key={`${detail.label}-${index}`}>
            <div className="mb-1 text-[11px] font-semibold uppercase text-gray-400">
              {detail.label}
            </div>
            {detail.kind === "code" ||
            detail.kind === "json" ||
            detail.kind === "log" ? (
              <pre className="max-h-48 overflow-auto rounded bg-gray-950 p-2 text-[11px] leading-5 text-gray-100">
                {detail.value}
              </pre>
            ) : (
              <p className="whitespace-pre-wrap text-xs leading-5 text-gray-700">
                {detail.value}
              </p>
            )}
          </div>
        ))}
      </div>
    </details>
  );
}

function ActivityRow({
  event,
  isActive,
}: {
  event: FreeformProgressEvent;
  isActive: boolean;
}) {
  const details = eventDetails(event);
  const title =
    event.type === "reasoning-summary"
      ? "Analyzing project data"
      : event.type === "partial-summary"
        ? "Summary preview is ready"
        : event.type === "partial-finding"
          ? `Finding ${event.index + 1} preview is ready`
          : eventLabel(event);
  const subtitle =
    event.type === "tool-progress"
      ? event.eventType
      : event.type === "status"
        ? event.status
        : event.type === "reasoning-summary"
          ? truncateInline(event.summary)
          : event.type === "partial-summary"
            ? truncateInline(event.summary)
            : event.type === "partial-finding"
              ? truncateInline(event.finding.finding)
              : undefined;

  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 shadow-sm">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-gray-50">
          <ActivityIcon event={event} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <ActivityBadge event={event} />
            <div className="min-w-0 text-sm font-medium text-gray-800">
              {title}
              {isActive && <AnimatedDots />}
            </div>
          </div>
          {subtitle && (
            <div className="mt-0.5 break-words text-xs text-gray-500">
              {subtitle}
            </div>
          )}
          <ActivityDetails details={details} />
        </div>
      </div>
    </div>
  );
}

function AnimatedDots() {
  const [dotCount, setDotCount] = useState(1);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setDotCount((current) => (current >= 3 ? 1 : current + 1));
    }, 450);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <span className="inline-block w-5 text-left" aria-hidden="true">
      {".".repeat(dotCount)}
    </span>
  );
}

function ProgressPanel({
  events,
  apiProgress,
  reasoningSummary,
  isRunning,
}: {
  events: FreeformProgressEvent[];
  apiProgress: ReturnType<typeof useFreeformReview>["apiProgress"];
  reasoningSummary: string | null;
  isRunning: boolean;
}) {
  const streaming = apiProgress?.phase === "streaming";
  const visibleEventsBase = events
    .filter(
      (event) =>
        event.type === "status" ||
        event.type === "tool-progress" ||
        event.type === "reasoning-summary" ||
        event.type === "error",
    )
    .slice(-4);
  const visibleEvents =
    reasoningSummary &&
    !visibleEventsBase.some((event) => event.type === "reasoning-summary")
      ? [
          ...visibleEventsBase,
          {
            type: "reasoning-summary" as const,
            summary: reasoningSummary,
            details: [
              {
                label: "Summary",
                value: reasoningSummary,
                kind: "text" as const,
              },
            ],
          },
        ]
      : visibleEventsBase;
  const activeIndex = visibleEvents.length - 1;

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
      {reasoningSummary && !streaming && (
        <div className="mb-3 rounded-md border border-blue-100 bg-white p-3">
          <div className="mb-1 text-xs font-semibold text-blue-700">
            Analysis Summary
          </div>
          <p className="whitespace-pre-wrap text-xs leading-5 text-gray-700">
            {reasoningSummary}
          </p>
        </div>
      )}

      {streaming ? (
        <div className="grid gap-2 sm:grid-cols-3">
          <div className="rounded-md bg-white p-3">
            <div className="text-[11px] font-medium uppercase text-gray-400">
              Output tokens
            </div>
            <div className="mt-1 text-lg font-semibold text-gray-900">
              {(apiProgress?.tokensReceived ?? 0).toLocaleString()}
            </div>
          </div>
          <div className="rounded-md bg-white p-3">
            <div className="text-[11px] font-medium uppercase text-gray-400">
              Output data
            </div>
            <div className="mt-1 text-lg font-semibold text-gray-900">
              {formatBytes(apiProgress?.bytesReceived)}
            </div>
          </div>
          <div className="rounded-md bg-white p-3">
            <div className="text-[11px] font-medium uppercase text-gray-400">
              JSON chars
            </div>
            <div className="mt-1 text-lg font-semibold text-gray-900">
              {(apiProgress?.contentLength ?? 0).toLocaleString()}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {visibleEvents.length === 0 ? (
            <div className="text-sm text-gray-500">
              Waiting
              <AnimatedDots />
            </div>
          ) : (
            visibleEvents.map((event, index) => (
              <ActivityRow
                key={progressEventKey(event, index)}
                event={event}
                isActive={isRunning && index === activeIndex}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function FindingCard({
  finding,
  onProjectDataReferenceClick,
}: {
  finding: FreeformFinding;
  onProjectDataReferenceClick?: (source: string) => void;
}) {
  const findingReference =
    finding.json_path && hasProjectDataReference(finding.json_path)
      ? finding.json_path
      : null;

  return (
    <div className="rounded-md border border-gray-200 bg-white p-4">
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <span
          className={`rounded px-1.5 py-0.5 text-xs font-semibold ${findingBadgeClass(
            finding.severity,
          )}`}
        >
          {finding.severity}
        </span>
        <span
          className={`rounded px-1.5 py-0.5 text-xs font-semibold ${findingBadgeClass(
            finding.finding_type,
          )}`}
        >
          {finding.finding_type}
        </span>
        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-semibold text-gray-600">
          {finding.confidence}
        </span>
        {finding.day_number != null && (
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-semibold text-gray-600">
            Day {finding.day_number}
          </span>
        )}
      </div>
      <p className="whitespace-pre-wrap text-sm leading-6 text-gray-900">
        {finding.finding}
      </p>
      {(finding.item_reference || finding.json_path) && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
          {finding.item_reference && <span>{finding.item_reference}</span>}
          {finding.json_path && (
            <span className="font-mono">{finding.json_path}</span>
          )}
          {findingReference && onProjectDataReferenceClick && (
            <ProjectDataReferenceButton
              source={findingReference}
              onOpen={onProjectDataReferenceClick}
              className="py-0.5"
            />
          )}
        </div>
      )}
      {finding.evidence.length > 0 && (
        <details className="mt-3 rounded-md border border-gray-200 bg-gray-50">
          <summary className="flex cursor-pointer list-none items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-600 hover:text-gray-900">
            <ChevronDown size={13} aria-hidden="true" />
            Evidence ({finding.evidence.length})
          </summary>
          <div className="space-y-2 border-t border-gray-200 p-2">
            {finding.evidence.map((evidence, index) => {
              const referenceSource = freeformEvidenceReferenceSource(
                finding,
                evidence,
              );
              return (
                <div
                  key={`${evidence.source}-${evidence.json_path ?? ""}-${index}`}
                  className="rounded border border-gray-100 bg-white p-2 text-xs text-gray-700"
                >
                  <div className="mb-1 flex items-start justify-between gap-2">
                    <div className="min-w-0 break-words font-semibold text-gray-600">
                      {evidence.source}
                    </div>
                    {referenceSource && onProjectDataReferenceClick && (
                      <ProjectDataReferenceButton
                        source={referenceSource}
                        onOpen={onProjectDataReferenceClick}
                        className="py-0.5"
                      />
                    )}
                  </div>
                  <div className="whitespace-pre-wrap">{evidence.quote}</div>
                  {evidence.json_path && (
                    <div className="mt-1 break-all font-mono text-[11px] text-gray-400">
                      {evidence.json_path}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </details>
      )}
      <div className="mt-3 rounded border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
        {finding.recommendation}
      </div>
    </div>
  );
}

function FreeformUsagePopover({
  result,
  estimatedCost,
  onClose,
}: {
  result: FreeformCheckResult;
  estimatedCost: number | undefined;
  onClose: () => void;
}) {
  return (
    <>
      <button
        type="button"
        aria-label="Close LLM usage details"
        className="fixed inset-0 z-40 cursor-default"
        onClick={onClose}
      />
      <div className="absolute right-0 top-8 z-50 w-80 rounded-lg border border-gray-200 bg-white p-4 text-sm shadow-xl">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h4 className="font-bold text-gray-800">LLM Usage</h4>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-1.5 py-1 text-xs font-semibold text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            Close
          </button>
        </div>

        <div className="rounded-md bg-gray-50 p-3">
          <div className="flex justify-between items-baseline mb-1">
            <span className="text-xs font-medium text-gray-500">
              Total Tokens
            </span>
            <span className="font-bold text-gray-800">
              {result.tokensUsed.total.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between items-baseline text-xs text-gray-500">
            <span>
              {result.tokensUsed.prompt.toLocaleString()} in /{" "}
              {result.tokensUsed.completion.toLocaleString()} out
            </span>
            <span className="font-medium text-gray-700">
              {formatCost(estimatedCost)}
            </span>
          </div>
          <div className="mt-1 flex justify-between items-baseline text-xs text-gray-500">
            <span>Time</span>
            <span className="font-medium text-gray-700">
              {formatDuration(result.durationMs)}
            </span>
          </div>
        </div>

        <div className="mt-3 space-y-2">
          <div className="rounded-md border border-blue-100 bg-blue-50/50 p-2.5">
            <div className="text-[11px] font-semibold uppercase text-blue-400">
              Model
            </div>
            <div className="mt-1 break-all text-xs font-medium text-blue-950">
              {result.model ?? "-"}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="rounded-md border border-gray-200 bg-white p-2">
              <div className="text-gray-400">Input</div>
              <div className="mt-0.5 font-medium text-gray-700">
                {result.tokensUsed.prompt.toLocaleString()}
              </div>
            </div>
            <div className="rounded-md border border-gray-200 bg-white p-2">
              <div className="text-gray-400">Output</div>
              <div className="mt-0.5 font-medium text-gray-700">
                {result.tokensUsed.completion.toLocaleString()}
              </div>
            </div>
            <div className="rounded-md border border-gray-200 bg-white p-2">
              <div className="text-gray-400">Cost</div>
              <div className="mt-0.5 font-medium text-gray-700">
                {formatCost(estimatedCost)}
              </div>
            </div>
          </div>
          <div className="text-[11px] leading-4 text-gray-400">
            Cost is estimated from configured model pricing and token usage.
          </div>
        </div>
      </div>
    </>
  );
}

function FreeformResultPanel({
  result,
  onProjectDataReferenceClick,
}: {
  result: FreeformCheckResult;
  onProjectDataReferenceClick?: (source: string) => void;
}) {
  const estimatedCost = freeformResultCost(result);
  const [showUsage, setShowUsage] = useState(false);

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-gray-200 bg-white p-4">
        <div className="mb-1 flex items-start justify-between gap-3">
          <div className="text-xs font-semibold uppercase text-gray-400">
            Freeform Review Results
          </div>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowUsage((value) => !value)}
              className="inline-flex h-7 items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2 text-xs font-semibold text-gray-600 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
              title="LLM usage details"
            >
              <Info size={13} aria-hidden="true" />
              LLM usage
            </button>
            {showUsage && (
              <FreeformUsagePopover
                result={result}
                estimatedCost={estimatedCost}
                onClose={() => setShowUsage(false)}
              />
            )}
          </div>
        </div>
        <h3 className="text-base font-semibold text-gray-900">
          {result.stage}
        </h3>
        <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-gray-700">
          {result.summary}
        </p>
        <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-500">
          <span>{result.findings.length} findings</span>
        </div>
      </div>
      {result.reasoningSummary && (
        <details className="rounded-md border border-blue-100 bg-blue-50 p-3 text-sm">
          <summary className="cursor-pointer font-semibold text-blue-800">
            Analysis Summary
          </summary>
          <p className="mt-2 whitespace-pre-wrap leading-6 text-blue-950">
            {result.reasoningSummary}
          </p>
        </details>
      )}
      <div className="space-y-3">
        {result.findings.map((finding, index) => (
          <FindingCard
            key={findingCardKey(finding, index)}
            finding={finding}
            onProjectDataReferenceClick={onProjectDataReferenceClick}
          />
        ))}
      </div>
    </div>
  );
}

function FreeformPartialResultPanel({
  partialResult,
  isRunning,
  onProjectDataReferenceClick,
}: {
  partialResult: FreeformPartialResult;
  isRunning: boolean;
  onProjectDataReferenceClick?: (source: string) => void;
}) {
  const hasSummary = Boolean(partialResult.summary?.trim());
  const hasFindings = partialResult.findings.length > 0;
  if (!hasSummary && !hasFindings) return null;

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <div className="text-xs font-semibold uppercase text-emerald-700">
            Streaming Preview
          </div>
          <span className="rounded bg-white px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-100">
            {isRunning ? (
              <>
                updating
                <AnimatedDots />
              </>
            ) : (
              "preview"
            )}
          </span>
        </div>
        <h3 className="text-base font-semibold text-emerald-950">
          {partialResult.stage}
        </h3>
        {hasSummary ? (
          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-emerald-950">
            {partialResult.summary}
          </p>
        ) : (
          <p className="mt-2 text-sm leading-6 text-emerald-800">
            Waiting for the summary to complete.
          </p>
        )}
        <div className="mt-3 flex flex-wrap gap-3 text-xs text-emerald-800">
          <span>{partialResult.findings.length} findings received</span>
          <span>
            {isRunning
              ? "Final validation will replace this preview."
              : "This preview is not a validated final result."}
          </span>
        </div>
      </div>
      {hasFindings && (
        <div className="space-y-3">
          {partialResult.findings.map((finding, index) => (
            <FindingCard
              key={`partial:${findingCardKey(finding, index)}`}
              finding={finding}
              onProjectDataReferenceClick={onProjectDataReferenceClick}
            />
          ))}
        </div>
      )}
      {isRunning && (
        <div className="rounded-md border border-dashed border-emerald-300 bg-white px-4 py-3">
          <div className="flex items-center gap-3">
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50"
              aria-hidden="true"
            >
              <span className="h-2.5 w-2.5 animate-ping rounded-full bg-emerald-500" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-emerald-900">
                Still receiving results
                <AnimatedDots />
              </div>
              <div className="mt-0.5 text-xs leading-5 text-emerald-700">
                More findings may appear until the final validated result
                replaces this preview.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

type RulePopupMode = "download" | "import" | "restore";

function toggleIdSelection(selectedIds: string[], id: string): string[] {
  return selectedIds.includes(id)
    ? selectedIds.filter((selectedId) => selectedId !== id)
    : [...selectedIds, id];
}

function RuleDialog({
  title,
  description,
  maxWidth = "max-w-lg",
  onClose,
  children,
}: {
  title: string;
  description: string;
  maxWidth?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/25 p-4">
      <div
        className={`w-full ${maxWidth} rounded-lg border border-gray-200 bg-white p-4 shadow-xl`}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold text-gray-900">{title}</h3>
            <p className="mt-1 text-xs leading-5 text-gray-500">
              {description}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-1 text-xs font-semibold text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          >
            Close
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function RuleSelectionList({
  items,
  selectedIds,
  tone = "default",
  onToggle,
}: {
  items: Array<{ id: string; label: string }>;
  selectedIds: string[];
  tone?: "default" | "import";
  onToggle: (id: string) => void;
}) {
  const panelClass =
    tone === "import"
      ? "border-blue-100 bg-blue-50"
      : "border-gray-200 bg-gray-50";
  const textClass = tone === "import" ? "text-blue-950" : "text-gray-700";

  return (
    <div
      className={`max-h-80 space-y-1 overflow-auto rounded-md border p-2 ${panelClass}`}
    >
      {items.map((item) => (
        <label
          key={item.id}
          className={`flex items-start gap-2 rounded px-2 py-1.5 text-sm hover:bg-white ${textClass}`}
        >
          <input
            type="checkbox"
            checked={selectedIds.includes(item.id)}
            onChange={() => onToggle(item.id)}
            className="mt-1"
          />
          <span className="min-w-0 break-words">{item.label}</span>
        </label>
      ))}
    </div>
  );
}

function ruleItemFromTemplate(template: FreeformCheckTemplate): {
  id: string;
  label: string;
} {
  return { id: template.id, label: template.stage };
}

export function FreeformReviewPanel({
  projectId,
  projectData,
  language,
  source,
  initialTemplateId,
  initialPrompt,
  onProjectDataReferenceClick,
  onResultChange,
}: FreeformReviewPanelProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [templateStatus, setTemplateStatus] = useState<string | null>(null);
  const review = useFreeformReview({
    projectId,
    language,
    source,
    initialTemplateId,
    initialPrompt,
  });
  const [isStageMenuOpen, setIsStageMenuOpen] = useState(false);
  const [rulePopup, setRulePopup] = useState<RulePopupMode | null>(null);
  const [copyTemplateId, setCopyTemplateId] = useState<string | null>(null);
  const [copyStage, setCopyStage] = useState("");
  const [stageEditingTemplateId, setStageEditingTemplateId] = useState<
    string | null
  >(null);
  const [descriptionEditingTemplateId, setDescriptionEditingTemplateId] =
    useState<string | null>(null);
  const [selectedBundleTemplateIds, setSelectedBundleTemplateIds] = useState<
    string[]
  >([]);
  const [selectedRestoreTemplateIds, setSelectedRestoreTemplateIds] = useState<
    string[]
  >([]);
  const [pendingImportBundle, setPendingImportBundle] =
    useState<FreeformTemplateExportBundle | null>(null);
  const [selectedImportTemplateIds, setSelectedImportTemplateIds] = useState<
    string[]
  >([]);

  const activeTemplateIds = useMemo(
    () => review.templates.map((template) => template.id),
    [review.templates],
  );
  const standardTemplateIds = useMemo(
    () => review.standardTemplates.map((template) => template.id),
    [review.standardTemplates],
  );
  const copySourceTemplate = useMemo(
    () =>
      copyTemplateId
        ? (review.templates.find(
            (template) => template.id === copyTemplateId,
          ) ?? null)
        : null,
    [copyTemplateId, review.templates],
  );
  const exportRuleItems = useMemo(
    () => review.templates.map(ruleItemFromTemplate),
    [review.templates],
  );
  const restoreRuleItems = useMemo(
    () => review.standardTemplates.map(ruleItemFromTemplate),
    [review.standardTemplates],
  );
  const importRuleItems = useMemo(
    () =>
      pendingImportBundle?.templates
        .filter((template) => !template.deleted)
        .map((template) => ({
          id: template.templateId,
          label: template.stage,
        })) ?? [],
    [pendingImportBundle],
  );
  const selectedTemplateId = review.selectedTemplateId;
  const isStageEditing = stageEditingTemplateId === selectedTemplateId;
  const isDescriptionEditing =
    descriptionEditingTemplateId === selectedTemplateId;

  useEffect(() => {
    setSelectedBundleTemplateIds((current) =>
      current.length > 0
        ? current.filter((id) => activeTemplateIds.includes(id))
        : activeTemplateIds,
    );
  }, [activeTemplateIds]);

  useEffect(() => {
    setSelectedRestoreTemplateIds((current) =>
      current.length > 0
        ? current.filter((id) => standardTemplateIds.includes(id))
        : standardTemplateIds,
    );
  }, [standardTemplateIds]);

  useEffect(() => {
    if (review.result) onResultChange?.(review.result);
  }, [onResultChange, review.result]);

  const handleSavePrompt = () => {
    review.savePrompt();
    setStageEditingTemplateId(null);
    setDescriptionEditingTemplateId(null);
    setTemplateStatus("Rule saved locally.");
  };

  const handleAddRule = () => {
    review.addTemplate();
    setIsStageMenuOpen(false);
    setTemplateStatus("New rule added locally.");
  };

  const handleDeleteRule = (templateId = review.selectedTemplate?.id) => {
    if (!templateId || !review.deleteTemplate(templateId)) return;
    setTemplateStatus("Selected rule deleted locally.");
  };

  const handleOpenCopyRule = (templateId: string) => {
    const source = review.templates.find(
      (template) => template.id === templateId,
    );
    if (!source) return;
    setCopyTemplateId(templateId);
    setCopyStage(`${source.stage} Copy`);
    setIsStageMenuOpen(false);
  };

  const handleConfirmCopyRule = () => {
    if (!copyTemplateId) return;
    if (!review.copyTemplate(copyTemplateId, copyStage)) {
      setTemplateStatus("Enter a stage name before copying.");
      return;
    }
    setTemplateStatus("Rule copied locally.");
    setCopyTemplateId(null);
    setCopyStage("");
  };

  const handleRestoreSelectedDefaults = () => {
    const restored = review.restoreDefaultTemplates(selectedRestoreTemplateIds);
    setRulePopup(null);
    setTemplateStatus(
      restored > 0
        ? `Restored ${restored} standard rules.`
        : "No local changes matched the selected standard rules.",
    );
  };

  const handleExport = () => {
    const bundle = createFreeformTemplateExportBundle(
      review.templates,
      selectedBundleTemplateIds,
    );
    const blob = new Blob([JSON.stringify(bundle, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `freeform-review-templates-${new Date()
      .toISOString()
      .slice(0, 10)}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    setRulePopup(null);
    setTemplateStatus(`Exported ${bundle.templates.length} rules.`);
  };

  const handleImportFile = async (file: File) => {
    try {
      const text = await file.text();
      const bundle = parseFreeformTemplateBundle(JSON.parse(text) as unknown);
      const importableIds = bundle.templates
        .filter((template) => !template.deleted)
        .map((template) => template.templateId);
      if (importableIds.length === 0) {
        throw new Error("Import file does not include any rules to import.");
      }
      setPendingImportBundle(bundle);
      setSelectedImportTemplateIds(importableIds);
      setRulePopup("import");
      setTemplateStatus(
        `Loaded ${bundle.templates.length} rules. Select rules to import.`,
      );
    } catch (error) {
      setTemplateStatus(
        error instanceof Error ? error.message : "Template import failed.",
      );
    }
  };

  const handleConfirmImport = () => {
    if (!pendingImportBundle) return;
    const count = importFreeformTemplateBundle(
      pendingImportBundle,
      selectedImportTemplateIds,
    );
    review.reloadTemplates();
    setPendingImportBundle(null);
    setSelectedImportTemplateIds([]);
    setRulePopup(null);
    setTemplateStatus(`Imported ${count} rules.`);
  };

  const canRun =
    !review.isRunning &&
    Boolean(review.selectedTemplate) &&
    Boolean(projectData) &&
    review.stage.trim().length > 0 &&
    review.description.trim().length > 0 &&
    review.prompt.trim().length > 0;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]">
        <div className="space-y-4">
          <div className="relative">
            <div className="mb-1 flex items-center justify-between gap-2">
              <div className="text-xs font-semibold uppercase text-gray-500">
                Stage
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsStageMenuOpen(false);
                  setStageEditingTemplateId((current) =>
                    current === selectedTemplateId ? null : selectedTemplateId,
                  );
                }}
                disabled={!review.selectedTemplate}
                className={`inline-flex h-7 w-7 items-center justify-center rounded-md border text-xs transition-colors disabled:opacity-40 ${
                  isStageEditing
                    ? "border-blue-200 bg-blue-50 text-blue-700"
                    : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                }`}
                title={isStageEditing ? "Stop editing stage" : "Edit stage"}
              >
                <Pencil size={13} />
              </button>
            </div>
            {isStageEditing ? (
              <input
                id="freeform-stage"
                value={review.stage}
                onChange={(event) => review.setStage(event.target.value)}
                className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            ) : (
              <button
                type="button"
                onClick={() => setIsStageMenuOpen((value) => !value)}
                className="flex min-h-10 w-full items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-900 shadow-sm hover:bg-gray-50 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <span className="min-w-0 break-words">
                  {review.selectedTemplate?.stage || "Select a stage"}
                </span>
                <ChevronDown size={15} className="shrink-0 text-gray-400" />
              </button>
            )}
            {!isStageEditing && isStageMenuOpen && (
              <>
                <button
                  type="button"
                  aria-label="Close stage menu"
                  className="fixed inset-0 z-20 cursor-default"
                  onClick={() => setIsStageMenuOpen(false)}
                />
                <div className="absolute left-0 z-30 mt-2 w-[min(36rem,calc(100vw-2rem))] rounded-lg border border-gray-200 bg-white p-2 shadow-xl">
                  <div className="max-h-80 overflow-auto">
                    {review.templates.map((template) => {
                      const selected =
                        template.id === review.selectedTemplateId;
                      return (
                        <div
                          key={template.id}
                          className={`flex items-start gap-1 rounded-md p-1 ${
                            selected ? "bg-blue-50" : "hover:bg-gray-50"
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              review.setSelectedTemplateId(template.id);
                              setIsStageMenuOpen(false);
                              setTemplateStatus(null);
                            }}
                            className="min-w-0 flex-1 rounded px-2 py-1.5 text-left"
                          >
                            <div className="break-words text-sm font-semibold text-gray-900">
                              {template.stage}
                            </div>
                            <div className="mt-0.5 line-clamp-2 break-words text-xs leading-5 text-gray-500">
                              {template.description}
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleOpenCopyRule(template.id)}
                            className="mt-1 inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-white hover:text-blue-700"
                            title="Copy rule"
                          >
                            <Clipboard size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteRule(template.id)}
                            className="mt-1 inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-red-50 hover:text-red-700"
                            title="Delete rule"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      onClick={handleAddRule}
                      className="mt-1 flex w-full items-center gap-2 rounded-md border border-dashed border-gray-300 px-3 py-2 text-left text-sm font-semibold text-gray-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                    >
                      <Plus size={14} />
                      Add Freeform Review Rule
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleSavePrompt}
              disabled={!review.selectedTemplate || !review.canSaveTemplate}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              title="Save rule locally"
            >
              <Save size={14} />
              Save
            </button>
            <button
              type="button"
              onClick={() => setRulePopup("download")}
              disabled={review.templates.length === 0}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              title="Download selected rules"
            >
              <Download size={14} />
              Download
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              title="Import rules"
            >
              <Upload size={14} />
              Import
            </button>
            <button
              type="button"
              onClick={() => setRulePopup("restore")}
              disabled={review.standardTemplates.length === 0}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              title="Restore standard rules"
            >
              <RotateCcw size={14} />
              Restore Defaults
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) void handleImportFile(file);
              }}
            />
          </div>

          <div className="text-xs text-gray-500">
            {review.localRuleCount.toLocaleString()} local rule changes
          </div>
          {templateStatus && (
            <div className="rounded-md bg-gray-100 px-2 py-1.5 text-xs text-gray-600">
              {templateStatus}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div>
            <div className="mb-1 flex items-center justify-between gap-2">
              {/* textarea は編集モード時のみ存在するため、for は編集中だけ付ける */}
              <label
                htmlFor={
                  isDescriptionEditing ? "freeform-description" : undefined
                }
                className="block text-xs font-semibold uppercase text-gray-500"
              >
                Description
              </label>
              <button
                type="button"
                onClick={() =>
                  setDescriptionEditingTemplateId((current) =>
                    current === selectedTemplateId ? null : selectedTemplateId,
                  )
                }
                disabled={!review.selectedTemplate}
                className={`inline-flex h-7 w-7 items-center justify-center rounded-md border text-xs transition-colors disabled:opacity-40 ${
                  isDescriptionEditing
                    ? "border-blue-200 bg-blue-50 text-blue-700"
                    : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                }`}
                title={
                  isDescriptionEditing
                    ? "Stop editing description"
                    : "Edit description"
                }
              >
                <Pencil size={13} />
              </button>
            </div>
            {isDescriptionEditing ? (
              <textarea
                id="freeform-description"
                value={review.description}
                onChange={(event) => review.setDescription(event.target.value)}
                className="min-h-20 w-full resize-y rounded-md border border-gray-300 bg-white p-3 text-sm leading-6 text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            ) : (
              <div className="whitespace-pre-wrap break-words text-sm leading-5 text-gray-600">
                {review.description || "No description"}
              </div>
            )}
          </div>

          <div>
            <label
              htmlFor="freeform-prompt"
              className="mb-1 block text-xs font-semibold uppercase text-gray-500"
            >
              Prompt
            </label>
            <textarea
              id="freeform-prompt"
              value={review.prompt}
              onChange={(event) => review.setPrompt(event.target.value)}
              className="min-h-52 w-full resize-y rounded-md border border-gray-300 bg-white p-3 font-mono text-sm leading-6 text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => {
                onResultChange?.(null);
                void review.runReview({
                  projectId,
                  projectData,
                  language,
                  source,
                });
              }}
              disabled={!canRun}
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-blue-600 px-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Play size={15} />
              Run Freeform Review
            </button>
            {review.isRunning && (
              <button
                type="button"
                onClick={review.cancelReview}
                className="inline-flex h-9 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                <Square size={14} />
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>

      {copyTemplateId && copySourceTemplate && (
        <RuleDialog
          title="Copy Rule"
          description="Save the selected Freeform Review rule under another stage name."
          maxWidth="max-w-md"
          onClose={() => {
            setCopyTemplateId(null);
            setCopyStage("");
          }}
        >
          <label
            htmlFor="freeform-copy-stage"
            className="mb-1 block text-xs font-semibold uppercase text-gray-500"
          >
            Stage
          </label>
          <input
            id="freeform-copy-stage"
            value={copyStage}
            onChange={(event) => setCopyStage(event.target.value)}
            className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setCopyTemplateId(null);
                setCopyStage("");
              }}
              className="inline-flex h-9 items-center rounded-md border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmCopyRule}
              disabled={!copyStage.trim()}
              className="inline-flex h-9 items-center rounded-md bg-blue-600 px-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40"
            >
              Save Copy
            </button>
          </div>
        </RuleDialog>
      )}

      {rulePopup && (
        <RuleDialog
          title={
            rulePopup === "download"
              ? "Download Rules"
              : rulePopup === "import"
                ? "Import Rules"
                : "Restore Defaults"
          }
          description={
            rulePopup === "download"
              ? "Select the Freeform Review rules to include in the bundle."
              : rulePopup === "import"
                ? "Select the Freeform Review rules to import."
                : "Select the standard rules to restore."
          }
          onClose={() => {
            setRulePopup(null);
            if (rulePopup === "import") setPendingImportBundle(null);
          }}
        >
          {rulePopup === "download" && (
            <>
              <RuleSelectionList
                items={exportRuleItems}
                selectedIds={selectedBundleTemplateIds}
                onToggle={(id) =>
                  setSelectedBundleTemplateIds((current) =>
                    toggleIdSelection(current, id),
                  )
                }
              />
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setRulePopup(null)}
                  className="inline-flex h-9 items-center rounded-md border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleExport}
                  disabled={selectedBundleTemplateIds.length === 0}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md bg-blue-600 px-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40"
                >
                  <Download size={14} />
                  Download
                </button>
              </div>
            </>
          )}

          {rulePopup === "import" && pendingImportBundle && (
            <>
              <RuleSelectionList
                items={importRuleItems}
                selectedIds={selectedImportTemplateIds}
                tone="import"
                onToggle={(id) =>
                  setSelectedImportTemplateIds((current) =>
                    toggleIdSelection(current, id),
                  )
                }
              />
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setRulePopup(null);
                    setPendingImportBundle(null);
                  }}
                  className="inline-flex h-9 items-center rounded-md border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmImport}
                  disabled={selectedImportTemplateIds.length === 0}
                  className="inline-flex h-9 items-center rounded-md bg-blue-600 px-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40"
                >
                  Import Selected
                </button>
              </div>
            </>
          )}

          {rulePopup === "restore" && (
            <>
              <RuleSelectionList
                items={restoreRuleItems}
                selectedIds={selectedRestoreTemplateIds}
                onToggle={(id) =>
                  setSelectedRestoreTemplateIds((current) =>
                    toggleIdSelection(current, id),
                  )
                }
              />
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setRulePopup(null)}
                  className="inline-flex h-9 items-center rounded-md border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleRestoreSelectedDefaults}
                  disabled={selectedRestoreTemplateIds.length === 0}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md bg-blue-600 px-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40"
                >
                  <RotateCcw size={14} />
                  Restore Selected
                </button>
              </div>
            </>
          )}
        </RuleDialog>
      )}

      {(review.isRunning || review.progressEvents.length > 0) && (
        <ProgressPanel
          events={review.progressEvents}
          apiProgress={review.apiProgress}
          reasoningSummary={review.reasoningSummary}
          isRunning={review.isRunning}
        />
      )}

      {review.error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {review.error}
        </div>
      )}

      {!review.result && review.partialResult && (
        <FreeformPartialResultPanel
          partialResult={review.partialResult}
          isRunning={review.isRunning}
          onProjectDataReferenceClick={onProjectDataReferenceClick}
        />
      )}

      {review.result && (
        <FreeformResultPanel
          result={review.result}
          onProjectDataReferenceClick={onProjectDataReferenceClick}
        />
      )}
      {review.result && (
        <PromoteToTemplate
          prompt={review.prompt}
          defaultStage={review.result.stage}
          defaultDescription={review.result.templateName}
        />
      )}
    </div>
  );
}

// ─── ia-ui Step2: 定型テンプレートへの昇格 ────────────────────────────────────

const PROMOTE_STAGES = [
  "inquiry",
  "proposal",
  "quotation",
  "booking",
  "final",
  "post",
] as const;

/**
 * アドホックに使った質問を再利用可能な定型テンプレート（サーバ config）へ
 * 昇格させる導線。config_admin にのみ表示。
 */
function PromoteToTemplate({
  prompt,
  defaultStage,
  defaultDescription,
}: {
  prompt: string;
  defaultStage: string;
  defaultDescription: string;
}) {
  const { identity } = useAICheckLocalIdentity();
  const canPromote =
    identity?.roles.includes("aicheck_admin") ||
    identity?.roles.includes("config_admin");
  const [open, setOpen] = useState(false);
  const [id, setId] = useState("");
  const [stage, setStage] = useState(
    (PROMOTE_STAGES as readonly string[]).includes(defaultStage)
      ? defaultStage
      : "proposal",
  );
  const [description, setDescription] = useState(defaultDescription);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!canPromote || !prompt.trim()) return null;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const token = await authService.getIdToken();
      if (!token) throw new Error("未認証です。再度サインインしてください。");
      const response = await fetch(apiUrl("/api/aicheck/freeform/promote"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          id: id.trim(),
          stage,
          description: description.trim(),
          prompt: prompt.trim(),
        }),
      });
      const data = (await response.json().catch(() => null)) as {
        ok?: boolean;
        error?: string;
      } | null;
      if (!response.ok || !data?.ok) {
        throw new Error(data?.error ?? `HTTP ${response.status}`);
      }
      setMessage(
        `テンプレート「${id.trim()}」として保存しました。次回からテンプレート一覧に表示されます。`,
      );
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "昇格に失敗しました。");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-violet-200 bg-violet-50/50 p-3">
      {message ? (
        <p className="text-sm text-violet-800">{message}</p>
      ) : (
        <>
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            className="text-sm font-semibold text-violet-700 hover:text-violet-900"
          >
            {open ? "▼" : "▶"} この質問を定型テンプレートへ昇格
          </button>
          <p className="mt-0.5 text-[11px] text-violet-500">
            良い質問だったらテンプレート化してチームで再利用できます（config
            に保存され、Config 画面から Git 反映も可能）。
          </p>
          {open && (
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex flex-wrap gap-3">
                <label className="flex flex-col gap-1 text-xs text-gray-500">
                  テンプレート ID（英小文字・数字・ハイフン）
                  <input
                    type="text"
                    value={id}
                    onChange={(event) => setId(event.target.value)}
                    placeholder="onsen-family-fit"
                    className="h-8 w-56 rounded border border-gray-300 px-2 text-sm"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs text-gray-500">
                  ステージ
                  <select
                    value={stage}
                    onChange={(event) => setStage(event.target.value)}
                    className="h-8 rounded border border-gray-300 bg-white px-2 text-sm"
                  >
                    {PROMOTE_STAGES.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="flex flex-col gap-1 text-xs text-gray-500">
                説明（テンプレート一覧に表示されます）
                <input
                  type="text"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  className="h-8 w-full rounded border border-gray-300 px-2 text-sm"
                />
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={busy || !id.trim() || !description.trim()}
                  onClick={() => void submit()}
                  className="rounded bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-50"
                >
                  {busy ? "保存中…" : "テンプレートとして保存"}
                </button>
                {error && <span className="text-xs text-red-600">{error}</span>}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
